import React, { useState, useRef, useMemo, useEffect } from "react";
import featureFlags from "../../config/featureFlags";
import { useDispatch, useSelector } from "react-redux";
import {
  Camera,
  User,
  Mail,
  Home,
  Globe,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  ArrowLeft,
  Check,
  Upload,
  FileText,
} from "lucide-react";
import { toast } from "react-toastify";
import { navigateTo } from "../../utils/navigation";
import api from "../../api/axiosInstance";
import {
  fetchCountries,
  fetchStates,
  fetchDistricts,
} from "../../features/master/masterThunks";
import { clearStates, clearDistricts } from "../../features/master/masterSlice";

const isValidUrl = (url) => {
  try {
    if (!url) return true; // optional fields
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const calculateAge = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const isEmpty = (val) => !val || !val.trim();

const isValidSocialUrl = (url) => {
  if (!url) return true;
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const isFutureDate = (date) => {
  return new Date(date) > new Date();
};



// ---------------- REUSABLE INPUT ----------------
const FormInput = ({ icon: Icon, isTextArea = false, className = "", hint, ...props }) => (
  <div className="form-group">
    <div className={`input-container ${isTextArea ? "large" : ""} ${className}`}>
      {Icon && <Icon className="input-icon" size={18} />}
      {isTextArea ? (
        <textarea className="form-input large" {...props} />
      ) : (
        <input className={`form-input ${className}`} {...props} />
      )}
    </div>
    {hint && (
      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginLeft: '4px' }}>
        {hint}
      </p>
    )}
  </div>
);

// ---------------- REUSABLE SELECT ----------------
const FormSelect = ({ icon: Icon, options = [], loading = false, ...props }) => (
  <div className="form-group">
    <div className="input-container">
      {Icon && <Icon className="input-icon" size={18} />}
      <select className="form-input" {...props}>
        <option value="">
          {loading ? "Loading..." : props.placeholder || "Select"}
        </option>
        {options.map((opt) => (
          <option key={opt._id || opt.id} value={opt._id || opt.id}>
            {opt.name}
          </option>
        ))}
      </select>
    </div>
  </div>
);

// ---------------- KYC DOCUMENT UPLOAD ----------------
const DocUpload = ({ label, file, inputRef, onChange }) => (
  <div className="form-group" onClick={() => inputRef.current?.click()} style={{ cursor: "pointer" }}>
    <input
      type="file"
      ref={inputRef}
      style={{ display: "none" }}
      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
      onChange={onChange}
    />
    <div className="input-container" style={{ cursor: "pointer" }}>
      <FileText className="input-icon" size={18} />
      <div
        className="form-input"
        style={{ display: "flex", alignItems: "center", color: file ? "#111827" : "#9ca3af", userSelect: "none" }}
      >
        {file ? file.name : label}
      </div>
      <span style={{ position: "absolute", right: 12, fontSize: 12, fontWeight: 500, color: "#6366f1" }}>
        {file ? "Change" : "Upload"}
      </span>
    </div>
    {file && (
      <p style={{ fontSize: 11, color: "#10b981", marginTop: 3, marginLeft: 4 }}>✓ {file.name}</p>
    )}
  </div>
);

// ---------------- LOCAL AREA SEARCH ----------------
const LocalAreaSearch = ({ districtId, countryId, stateId, onSelect, disabled }) => {
  const [inputValue, setInputValue]   = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen]           = useState(false);
  const [searching, setSearching]     = useState(false);
  const [saving, setSaving]           = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const debounceRef = useRef(null);
  const wrapperRef  = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset when district changes
  useEffect(() => {
    setInputValue("");
    setSelectedName("");
    setSuggestions([]);
    setIsOpen(false);
    onSelect("");
  }, [districtId]); // eslint-disable-line

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setSelectedName("");
    onSelect("");

    if (!districtId || !val.trim()) {
      setSuggestions([]);
      setIsOpen(false);
      clearTimeout(debounceRef.current);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(
          `/api/master/local-areas?districtId=${districtId}&name=${encodeURIComponent(val.trim())}`
        );
        const data = res.data?.data || [];
        setSuggestions(data);
        setIsOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleSelect = (area) => {
    setInputValue(area.name);
    setSelectedName(area.name);
    setSuggestions([]);
    setIsOpen(false);
    onSelect(area._id);
  };

  const handleAddNew = async () => {
    if (!inputValue.trim() || !districtId || !countryId || !stateId || saving) return;
    setSaving(true);
    try {
      const res = await api.post("/api/master/local-areas", {
        name: inputValue.trim(),
        countryId,
        stateId,
        districtId,
      });
      if (res.data?.success) {
        const newId = res.data.data.localAreaId;
        setSelectedName(inputValue.trim());
        setSuggestions([]);
        setIsOpen(false);
        onSelect(newId);
        toast.success(`"${inputValue.trim()}" added as new local area`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add local area");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setInputValue("");
    setSelectedName("");
    setSuggestions([]);
    setIsOpen(false);
    onSelect("");
  };

  const hasExactMatch = suggestions.some(
    (s) => s.name.toLowerCase() === inputValue.trim().toLowerCase()
  );
  const showAddOption = inputValue.trim().length > 0 && !hasExactMatch && !searching;

  return (
    <div className="form-group" ref={wrapperRef} style={{ position: "relative" }}>
      <div className="input-container">
        <MapPin className="input-icon" size={18} />
        <input
          className="form-input"
          type="text"
          placeholder={disabled ? "Select district first" : "Search Local Area (optional)"}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
          disabled={disabled || saving}
          autoComplete="off"
        />
        {searching && (
          <span style={{ position: "absolute", right: 12, color: "#9ca3af", fontSize: 12 }}>
            searching…
          </span>
        )}
        {selectedName && !searching && (
          <span
            onClick={handleClear}
            style={{ position: "absolute", right: 12, cursor: "pointer", color: "#9ca3af", fontSize: 16 }}
          >
            ✕
          </span>
        )}
      </div>

      {isOpen && (suggestions.length > 0 || showAddOption) && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 200,
          maxHeight: 200, overflowY: "auto"
        }}>
          {suggestions.map((area) => (
            <div
              key={area._id}
              onMouseDown={() => handleSelect(area)}
              style={{ padding: "10px 16px", cursor: "pointer", fontSize: 14, color: "#111827" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
            >
              {area.name}
            </div>
          ))}
          {showAddOption && (
            <div
              onMouseDown={handleAddNew}
              style={{
                padding: "10px 16px", cursor: saving ? "not-allowed" : "pointer",
                fontSize: 14, color: "#6366f1", fontWeight: 500,
                borderTop: suggestions.length > 0 ? "1px solid #e5e7eb" : "none",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
            >
              {saving ? "Adding…" : `+ Add "${inputValue.trim()}" as new area`}
            </div>
          )}
        </div>
      )}

      {selectedName && (
        <p style={{ fontSize: 12, color: "#10b981", marginTop: 4, marginLeft: 4 }}>
          ✓ {selectedName} selected
        </p>
      )}
    </div>
  );
};

const ProfileComplete = () => {
  const dispatch = useDispatch();
  const fileInputRef    = useRef(null);
  const aadhaarFrontRef = useRef(null);
  const aadhaarBackRef  = useRef(null);
  const panFrontRef     = useRef(null);
  const panBackRef      = useRef(null);

  // Redux state
  const { countries, states, districts, loading: masterLoading } = useSelector(
    (state) => state.master
  );
  const { user: authUser } = useSelector((state) => state.merchant);

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    photo: null,
    photoPreview: null,
    fullName: "",
    email: "",
    sonDaughterOf: "",
    dob: "",
    // KYC
    aadhaarNumber: "",
    panNumber: "",
    aadhaarFront: null,
    aadhaarBack: null,
    panFront: null,
    panBack: null,
    // Address
    streetAddress: "",
    countryId: "",
    stateId: "",
    districtId: "",
    localAreaId: "",
    // Social
    facebookProfile: "",
    twitterProfile: "",
    instagramProfile: "",
  });

  // Auto-fill fullName and email from logged-in user
  useEffect(() => {
    if (authUser) {
      setFormData(prev => ({
        ...prev,
        fullName: authUser.fullName || "",
        email: authUser.email || "",
      }));
    }
  }, [authUser]);

  // Fetch countries on mount
  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  // Fetch states when country changes
  useEffect(() => {
    if (formData.countryId) {
      dispatch(fetchStates(formData.countryId));
      // Clear dependent fields
      setFormData(prev => ({
        ...prev,
        stateId: "",
        districtId: "",
        localAreaId: ""
      }));
    } else {
      dispatch(clearStates());
    }
  }, [formData.countryId, dispatch]);

  // Fetch districts when state changes
  useEffect(() => {
    if (formData.stateId) {
      dispatch(fetchDistricts(formData.stateId));
      // Clear dependent fields
      setFormData(prev => ({
        ...prev,
        districtId: "",
        localAreaId: ""
      }));
    } else {
      dispatch(clearDistricts());
    }
  }, [formData.stateId, dispatch]);

  // Reset localAreaId when district changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, localAreaId: "" }));
  }, [formData.districtId]);

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload an image file");
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }

      updateFormData({
        photo: file,
        photoPreview: URL.createObjectURL(file)
      });
    }
  };

  const handleKycFile = (field) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = [
      'image/jpeg', 'image/jpg', 'image/png',
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPG, PNG, PDF, DOC or DOCX files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be less than 5MB");
      return;
    }
    updateFormData({ [field]: file });
  };

  // ---------------- VALIDATION ----------------
const validateStep = () => {
  /* =====================
     STEP 1: PERSONAL
  ====================== */
  if (currentStep === 1) {

    if (!formData.photo) {
      toast.error("Profile photo is required");
      return false;
    }

    if (isEmpty(formData.fullName)) {
      toast.error("Full name is required");
      return false;
    }

    if (formData.fullName.trim().length < 3) {
      toast.error("Full name must be at least 3 characters");
      return false;
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      toast.error("Please enter a valid email address");
      return false;
    }

    if (isEmpty(formData.sonDaughterOf)) {
      toast.error("Please enter parent / guardian name");
      return false;
    }

    if (formData.sonDaughterOf.trim().length < 3) {
      toast.error("Parent name must be at least 3 characters");
      return false;
    }

    if (!formData.dob) {
      toast.error("Please select date of birth");
      return false;
    }

    if (isFutureDate(formData.dob)) {
      toast.error("Date of birth cannot be in the future");
      return false;
    }

    const age = calculateAge(formData.dob);
    if (age < 18) {
      toast.error("You must be at least 18 years old");
      return false;
    }
  }

  /* =====================
     STEP 2: ADDRESS
  ====================== */
  if (currentStep === 2) {

    if (isEmpty(formData.streetAddress)) {
      toast.error("Please enter full address");
      return false;
    }

    if (formData.streetAddress.trim().length < 10) {
      toast.error("Address must be at least 10 characters");
      return false;
    }

    if (!formData.countryId) {
      toast.error("Please select country");
      return false;
    }

    if (!formData.stateId) {
      toast.error("Please select state");
      return false;
    }

    // District & Local Area are optional
  }

  /* =====================
     STEP 3: SOCIAL
  ====================== */
  if (currentStep === 3) {

    if (!isValidSocialUrl(formData.facebookProfile)) {
      toast.error("Invalid Facebook URL");
      return false;
    }

    if (!isValidSocialUrl(formData.twitterProfile)) {
      toast.error("Invalid Twitter / X URL");
      return false;
    }

    if (!isValidSocialUrl(formData.instagramProfile)) {
      toast.error("Invalid Instagram URL");
      return false;
    }
  }

  return true;
};



  // ---------------- SUBMIT ALL DATA ----------------
  const submitProfile = async () => {
    setLoading(true);
    
    try {
      const formDataToSend = new FormData();

      // Photo
      if (formData.photo) formDataToSend.append("photoUrl", formData.photo);

      // Basic info
      formDataToSend.append("fullName", formData.fullName.trim());
      if (formData.email.trim()) formDataToSend.append("email", formData.email.trim());
      formDataToSend.append("sonDaughterOf", formData.sonDaughterOf.trim());
      formDataToSend.append("dob", formData.dob);

      // KYC text fields (optional)
      if (formData.aadhaarNumber.trim()) formDataToSend.append("aadhaarNumber", formData.aadhaarNumber.trim());
      if (formData.panNumber.trim())     formDataToSend.append("panNumber", formData.panNumber.trim());

      // KYC document files (optional)
      if (formData.aadhaarFront) formDataToSend.append("aadhaarFront", formData.aadhaarFront);
      if (formData.aadhaarBack)  formDataToSend.append("aadhaarBack",  formData.aadhaarBack);
      if (formData.panFront)     formDataToSend.append("panFront",     formData.panFront);
      if (formData.panBack)      formDataToSend.append("panBack",      formData.panBack);

      // Address
      formDataToSend.append("streetAddress", formData.streetAddress.trim());
      formDataToSend.append("countryId", formData.countryId);
      formDataToSend.append("stateId", formData.stateId);
      if (formData.districtId)  formDataToSend.append("districtId",  formData.districtId);
      if (formData.localAreaId) formDataToSend.append("localAreaId", formData.localAreaId);

      // Social Profiles
      formDataToSend.append("facebookProfile",  formData.facebookProfile.trim()  || "");
      formDataToSend.append("twitterProfile",   formData.twitterProfile.trim()   || "");
      formDataToSend.append("instagramProfile", formData.instagramProfile.trim() || "");

      console.log("Submitting profile data...");

      const response = await api.post("/api/auth/profile", formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log("Profile saved successfully:", response.data);
      toast.success("Profile completed successfully! 🎉");
      
      setTimeout(() => {
        navigateTo(featureFlags.ENABLE_USER_SUBSCRIPTION ? "/buy-subscription" : "/dashboard");
      }, 1500);

    } catch (error) {
      console.error("Profile save error:", error);
      console.error("Error response:", error.response?.data);
      
      const errorMessage = 
        error.response?.data?.message || 
        error.response?.data?.error ||
        error.message || 
        "Failed to save profile. Please try again.";
        
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- HANDLE NEXT BUTTON ----------------
 const handleNext = async () => {
  if (loading || masterLoading) {
    toast.info("Please wait...");
    return;
  }

  if (!validateStep()) return;

  if (currentStep < 3) {
    setCurrentStep((prev) => prev + 1);
    return;
  }

  await submitProfile();
};
  // ---------------- HANDLE PREV BUTTON ----------------

  const prevStep = () => {
    if (loading) return;
    setCurrentStep((p) => Math.max(1, p - 1));
  };

  // ---------------- PROGRESS BAR ----------------
  const progressBar = useMemo(() => {
    const steps = [
      { number: 1, label: "Personal", icon: User },
      { number: 2, label: "Address", icon: Home },
      { number: 3, label: "Social", icon: Globe },
    ];

    return (
      <div className="progress-container">
        {steps.map((step, i) => {
          const Icon = step.icon;
          const done = currentStep > step.number;
          const active = currentStep === step.number;

          return (
            <div key={step.number} className="progress-item">
              <div className={`progress-circle ${done ? "completed" : active ? "active" : ""}`}>
                {done ? <Check size={16} /> : <Icon size={16} />}
              </div>
              <span className={`progress-label ${done || active ? "active" : ""}`}>
                {step.label}
              </span>
              {i < steps.length - 1 && (
                <div className={`progress-line ${done ? "completed" : ""}`} />
              )}
            </div>
          );
        })}
      </div>
    );
  }, [currentStep]);

  // ---------------- FORM ----------------
  const renderForm = () => {
    if (currentStep === 1)
      return (
        <>
          <div className="photo-upload" onClick={() => fileInputRef.current?.click()}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePhotoUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />

            <div className="photo-circle">
              {formData.photoPreview ? (
                <img src={formData.photoPreview} alt="user" className="user-photo" />
              ) : (
                <Camera size={40} />
              )}
              <div className="photo-overlay">
                <Upload size={22} />
                <span>Upload Photo</span>
              </div>
            </div>
          </div>

          <FormInput
            icon={User}
            placeholder="Full Name*"
            value={formData.fullName}
            onChange={(e) => updateFormData({ fullName: e.target.value })}
            required
          />

          <FormInput
            icon={Mail}
            type="email"
            placeholder="Email Address (optional)"
            value={formData.email}
            onChange={(e) => updateFormData({ email: e.target.value })}
          />

          <FormInput
            icon={User}
            placeholder="Son / Daughter of*"
            value={formData.sonDaughterOf}
            onChange={(e) => updateFormData({ sonDaughterOf: e.target.value })}
            required
          />

          <FormInput
            type="date"
            value={formData.dob}
            onChange={(e) => updateFormData({ dob: e.target.value })}
            max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().split('T')[0]; })()}
            hint="DD/MM/YYYY · You must be at least 18 years old"
            required
          />

          {/* KYC Section */}
          <div style={{ margin: "20px 0 12px", borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
            <p style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 4 }}>
              KYC Document <span style={{ fontWeight: 400, color: "#9ca3af" }}>(Optional)</span>
            </p>
            <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
              JPG, PNG, PDF, DOC accepted · Max 5MB each
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
            <FormInput
              icon={FileText}
              placeholder="Aadhaar Number"
              value={formData.aadhaarNumber}
              onChange={(e) => updateFormData({ aadhaarNumber: e.target.value })}
              maxLength={12}
            />

            <FormInput
              icon={FileText}
              placeholder="PAN Number"
              value={formData.panNumber}
              onChange={(e) => updateFormData({ panNumber: e.target.value.toUpperCase() })}
              maxLength={10}
            />

            <DocUpload
              label="Aadhaar Front"
              file={formData.aadhaarFront}
              inputRef={aadhaarFrontRef}
              onChange={handleKycFile("aadhaarFront")}
            />

            <DocUpload
              label="Aadhaar Back"
              file={formData.aadhaarBack}
              inputRef={aadhaarBackRef}
              onChange={handleKycFile("aadhaarBack")}
            />

            <DocUpload
              label="PAN Front"
              file={formData.panFront}
              inputRef={panFrontRef}
              onChange={handleKycFile("panFront")}
            />

            <DocUpload
              label="PAN Back"
              file={formData.panBack}
              inputRef={panBackRef}
              onChange={handleKycFile("panBack")}
            />
          </div>
        </>
      );

    if (currentStep === 2)
      return (
        <>
          <FormSelect
            icon={Globe}
            placeholder="Select Country*"
            options={countries}
            loading={masterLoading}
            value={formData.countryId}
            onChange={(e) => updateFormData({ countryId: e.target.value })}
            required
          />

          <FormSelect
            icon={MapPin}
            placeholder="Select State*"
            options={states}
            loading={masterLoading}
            value={formData.stateId}
            onChange={(e) => updateFormData({ stateId: e.target.value })}
            disabled={!formData.countryId}
            required
          />

          <FormSelect
            icon={MapPin}
            placeholder="Select District"
            options={districts}
            loading={masterLoading}
            value={formData.districtId}
            onChange={(e) => updateFormData({ districtId: e.target.value })}
            disabled={!formData.stateId}
          />

          <LocalAreaSearch
            districtId={formData.districtId}
            countryId={formData.countryId}
            stateId={formData.stateId}
            onSelect={(id) => updateFormData({ localAreaId: id })}
            disabled={!formData.districtId}
          />

          <FormInput
            isTextArea
            icon={Home}
            placeholder="Full Address*"
            value={formData.streetAddress}
            onChange={(e) => updateFormData({ streetAddress: e.target.value })}
            required
          />

          <FormInput
            icon={MapPin}
            placeholder="Pincode (optional)"
            value={formData.pincode}
            onChange={(e) => updateFormData({ pincode: e.target.value })}
            maxLength={10}
          />
        </>
      );

    return (
      <>
        <FormInput 
          icon={Facebook} 
          placeholder="Facebook URL (optional)"
          value={formData.facebookProfile}
          onChange={(e) => updateFormData({ facebookProfile: e.target.value })}
          type="url"
        />

        <FormInput 
          icon={Twitter} 
          placeholder="Twitter / X URL (optional)"
          value={formData.twitterProfile}
          onChange={(e) => updateFormData({ twitterProfile: e.target.value })}
          type="url"
        />

        <FormInput 
          icon={Instagram} 
          placeholder="Instagram URL (optional)"
          value={formData.instagramProfile}
          onChange={(e) => updateFormData({ instagramProfile: e.target.value })}
          type="url"
        />
      </>
    );
  };

  return (
    <div className="register-container">
      <div className="register-card">
        {currentStep > 1 && (
          <button 
            className="back-button" 
            onClick={prevStep}
            disabled={loading}
          >
            <ArrowLeft size={18} />
          </button>
        )}

        {progressBar}

        <div className="form-content">{renderForm()}</div>

        <div className="button-group">
          <button 
            className="primary-button" 
            onClick={handleNext} 
             disabled={loading || masterLoading}
          >
            {loading ? "Saving..." : currentStep === 3 ? "Complete Profile" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileComplete;